// The approver's three decisions on the one actionable row: Approve · Rejected · Revision Required. A note is required
// for the last two (they stay aria-disabled, described by the note's label, until it has words). The leader's row and
// the Total Rewards row pass their own fields as children, hold Approve until those are complete, and record an
// approval only (their tools take no decision).
import { useId, useState, type ReactNode } from "react";
import { ACTION_LABELS, Button, COMMON_LABELS, Field, Flex, Stack, SURFACE_LABELS, TextArea, type Blocked } from "@/mc";
import type { ApprovalKey, Decision, DecideInput } from "./CompensationTab";
import { APPROVAL_STATE_LABELS } from "./labels";

export interface ApproverControlsProps {
  kind: ApprovalKey;
  /** The row's own fields (evidence, approved amount and wording). */
  children?: ReactNode | undefined;
  /** Approve waits for the row's own fields. */
  approveBlocked?: Blocked | undefined;
  /** The leader and Total Rewards rows record an approval only; the HM and HRBP rows decide all three. */
  approveOnly?: boolean | undefined;
  /** The row's own values, merged into the decision. */
  extra?: (() => Partial<DecideInput>) | undefined;
  onDecide?: ((input: DecideInput) => Promise<unknown>) | undefined;
}

export function ApproverControls({ kind, children, approveBlocked, approveOnly, extra, onDecide }: ApproverControlsProps) {
  const base = useId();
  const noteId = `${base}-note`;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<Decision | null>(null);
  const needsNote: Blocked | undefined = note.trim() === "" ? { reason: COMMON_LABELS.required, describedBy: `${noteId}-l` } : undefined;
  const decide = async (decision: Decision) => {
    if (!onDecide) return;
    setBusy(decision);
    try {
      await onDecide({ key: kind, decision, ...(note.trim() ? { note: note.trim() } : {}), ...(decision === "approved" ? extra?.() ?? {} : {}) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <Stack gap={12} className="mc-pkg-ctl">
      {children}
      <Field label={SURFACE_LABELS.notes} id={noteId}>
        <TextArea value={note} onChange={(event) => setNote(event.target.value)} rows={2}  />
      </Field>
      <Flex gap={8} wrap>
        <Button tone="primary" busy={busy === "approved"} blocked={approveBlocked} onClick={() => { void decide("approved"); }} >
          {ACTION_LABELS.approve}
        </Button>
        {approveOnly ? null : <Button busy={busy === "rejected"} blocked={needsNote} onClick={() => { void decide("rejected"); }} >
          {APPROVAL_STATE_LABELS.rejected}
        </Button>}
        {approveOnly ? null : <Button busy={busy === "revision_required"} blocked={needsNote} onClick={() => { void decide("revision_required"); }} >
          {APPROVAL_STATE_LABELS.revisionRequired}
        </Button>}
      </Flex>
    </Stack>
  );
}
